import React, { FC, useMemo, useState, useCallback, useRef, useEffect, CSSProperties } from 'react';
import {
  fileListToFileArray,
  filterWithAllowedExtensions,
  filterWithMaxSize,
  getFileInfo,
} from '../utils/fileuploader';
import { NoFile } from '.././Upload/NoFile';
import { ChooseFile } from '.././Upload/ChooseFile';
import { FileInfo, IFileInfo, IUploadCompleteInfo, IUploadedFileInfo } from '.././Upload/FileInfo';
import classNames from 'classnames';
import { AlertDialog } from '../../Dialogs/AlertDialog';
import { useDidUpdateEffect } from 'hooks/useDidUpdateEffect';

export interface IFileUploadAreaCommonProps {
  uploadedFileInfo?: IUploadedFileInfo[];
  isMultiFileUpload?: boolean;
  /** Should be in KB's */
  maxFileSize?: number;
  chooseFileActionName?: string;
  allowedExtensions?: string[];
  invalidFileExtensionsMessage?: string;
  isDisabled?: boolean;
  disabledDownload?: boolean;
  /** Flush all the uploaded file and use whatever is there in uploadedFileInfo */
  reset?: boolean;
  holderClassName?: string;
  labelCssClass?: string;
  linkCssClass?: string;
  fileNameCssClass?: string;
  containerCssClass?: string;
  tabIndex?: number;
  requestedMaxHeight?: number;
  requestedMinHeight?: number;
  setMaxSize?: boolean;
  setMinSize?: boolean;
  fontCssStyles?: CSSProperties;
  onFileAreaWarningsShowing?: (showing: boolean) => void;
  onDownload?: (fileId: string, fileName: string) => void;
}

export interface IFileUploadAreaProps extends IFileUploadAreaCommonProps {
  onRemove?: (file: IUploadedFileInfo) => void;
  onComplete?: (file: IUploadedFileInfo) => void;
  onCompleteAll?: (allfiles: IUploadedFileInfo[]) => void;
  onStart?: (file: IUploadedFileInfo[]) => void;
}

export const FileUploadArea: FC<IFileUploadAreaProps> = props => {
  const defaultUploadedFiles: IFileInfo[] = useMemo(
    () => props.uploadedFileInfo?.map(f => ({ ...f, uploaded: true })) || [],
    [props.uploadedFileInfo]
  );

  useEffect(() => {
    setFileInfo(defaultUploadedFiles);
  }, [defaultUploadedFiles]);

  const [fileInfo, setFileInfo] = useState(defaultUploadedFiles);
  const [dragActive, setDragActive] = useState(false);
  const [warningMessages, setWarningMessages] = useState<string[]>([]);
  const triggerAllUploadComplete = useRef(false);
  const filesToProcessOnComplete = useRef<IFileInfo[]>([]);

  useDidUpdateEffect(() => {
    if (props.reset) {
      //These files has been provided by the host, so no need to invoke allUploadComplete.
      //We will invoke this callback only if the files has been uploaded using this uploader
      triggerAllUploadComplete.current = false;
      setFileInfo(defaultUploadedFiles);
    }
  }, [defaultUploadedFiles, props.reset]);

  useEffect(() => {
    if (props.onCompleteAll && triggerAllUploadComplete.current && fileInfo.every(f => f.uploaded)) {
      triggerAllUploadComplete.current = false;
      props.onCompleteAll(fileInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileInfo, props.onCompleteAll]);

  const updateFiles = useCallback(
    (fileList: FileList) => {
      if (fileList !== null) {
        let validFiles = fileListToFileArray(fileList).filter(
          //only if the file is not already uploded
          f => !fileInfo.find(i => i.fileName.toLowerCase() === f.name.toLowerCase() && f.size === i.fileSize)
        );
        let invalidFiles: File[] = [];
        let warnings: string[] = [];
        [validFiles, invalidFiles] = filterWithAllowedExtensions(validFiles, props.allowedExtensions);
        if (invalidFiles.length) {
          const warning =
            props.invalidFileExtensionsMessage ||
            `${invalidFiles.map(file => file.name).join(',')} has invalid file extension.`;
          warnings.push(warning);
        }
        if (props.maxFileSize) {
          [validFiles, invalidFiles] = filterWithMaxSize(props.maxFileSize, validFiles);
          if (invalidFiles.length) {
            const warning = invalidFiles.map(file => file.name).join(',');
            warnings.push(`'${warning}' is too large, maximum allowed size is ${props.maxFileSize}KB`);
          }
        }
        setWarningMessages(warnings);
        const uploadedfileInfos = getFileInfo(validFiles, !!props.isMultiFileUpload);
        props.onStart && props.onStart(uploadedfileInfos);
        if (props.isMultiFileUpload) setFileInfo(preInfo => [...preInfo, ...uploadedfileInfos]);
        else setFileInfo(uploadedfileInfos);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      props.allowedExtensions,
      props.maxFileSize,
      props.isMultiFileUpload,
      props.invalidFileExtensionsMessage,
      fileInfo,
      props.onStart,
    ]
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = event.target.files;
      if (fileList !== null) updateFiles(fileList);
      event.target.value = '';
    },
    [updateFiles]
  );

  useEffect(() => {
    if (filesToProcessOnComplete.current.length) {
      filesToProcessOnComplete.current.forEach(element => {
        props.onComplete && element && props.onComplete({ ...element });
      });
      filesToProcessOnComplete.current = [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileInfo, props.onComplete]);

  const handleUploadComplete = useCallback(
    (updatedInfo: IUploadCompleteInfo) => {
      triggerAllUploadComplete.current = true;
      setFileInfo(preInfo =>
        preInfo.map(file => {
          if (file.fileId === updatedInfo.tempFileId && !file.uploaded) {
            const updateFileInfo = {
              ...file,
              fileId: updatedInfo.fileId,
              uploaded: true,
              file: undefined,
            };
            filesToProcessOnComplete.current.push(updateFileInfo);
            return updateFileInfo;
          }
          return file;
        })
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.onComplete]
  );

  const handleFileRemove = useCallback(
    (info: IUploadedFileInfo) => {
      triggerAllUploadComplete.current = false;
      setFileInfo(preInfo => preInfo.filter(f => f.fileId !== info.fileId));
      props.onRemove && props.onRemove(info);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.onRemove]
  );

  const handleDragEnter = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (props.isDisabled || (!props.isMultiFileUpload && fileInfo.length)) return;
      setDragActive(true);
    },
    [fileInfo.length, props.isDisabled, props.isMultiFileUpload]
  );

  const handleDragLeave = useCallback(() => {
    if (props.isDisabled) return;
    setDragActive(false);
  }, [props.isDisabled]);

  const handleWarningClose = useCallback(() => {
    setWarningMessages([]);
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setDragActive(false);
      if (props.isDisabled || (!props.isMultiFileUpload && fileInfo.length)) return;
      const fileList = event.dataTransfer.files;
      if (fileList !== null) updateFiles(fileList);
    },
    [props.isDisabled, props.isMultiFileUpload, fileInfo.length, updateFiles]
  );

  const fileInfoView = useMemo(() => {
    if (!fileInfo || !fileInfo.length) return null;
    return (
      <div className="dp-file-upload-area__files">
        {fileInfo.map(info => (
          <FileInfo
            key={info.fileId}
            fileInfo={info}
            onRemove={handleFileRemove}
            onComplete={handleUploadComplete}
            onDownload={props.onDownload}
            isDisabled={props.isDisabled}
            disabledDownload={props.disabledDownload}
            fileNameClassName={props.fileNameCssClass}
            fontCssStyles={props.fontCssStyles}
          />
        ))}
      </div>
    );
  }, [
    fileInfo,
    handleFileRemove,
    handleUploadComplete,
    props.onDownload,
    props.isDisabled,
    props.disabledDownload,
    props.fileNameCssClass,
    props.fontCssStyles,
  ]);

  const holderClassNames = useMemo(() => {
    return classNames(
      'dp-file-upload-area',
      props.holderClassName,
      { 'dp-file-upload-area__single': !props.isMultiFileUpload },
      { 'dp-file-upload-area__multiple': props.isMultiFileUpload },
      { 'dp-file-upload-area__drag--active': dragActive }
    );
  }, [props.isMultiFileUpload, dragActive, props.holderClassName]);

  const uploadAreaView = useMemo(() => {
    const showChooseAction = props.isMultiFileUpload || !fileInfo.length;

    return (
      <div className={holderClassNames} onDragOver={handleDragEnter} onDragLeave={handleDragLeave} onDrop={handleDrop}>
        {!fileInfo.length && <NoFile isMultiFileUpload={props.isMultiFileUpload} fontCssStyles={props.fontCssStyles} />}
        {fileInfoView}
        {showChooseAction && (
          <ChooseFile
            name={props.chooseFileActionName}
            onChange={handleFileChange}
            isMultiFileUpload={props.isMultiFileUpload}
            isDisabled={props.isDisabled}
            linkCssClass={props.linkCssClass}
            tabIndex={props.tabIndex}
            fontCssStyles={props.fontCssStyles}
          />
        )}
      </div>
    );
  }, [
    props.isMultiFileUpload,
    props.chooseFileActionName,
    props.isDisabled,
    props.linkCssClass,
    props.tabIndex,
    fileInfo.length,
    holderClassNames,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    fileInfoView,
    handleFileChange,
    props.fontCssStyles,
  ]);

  const warningDialogView = useMemo(() => {
    if (!warningMessages.length) return null;
    return (
      <AlertDialog
        open={true}
        onClose={handleWarningClose}
        messages={warningMessages}
        type="Warning"
        title="Warning"
        actionVariant="material"
      />
    );
  }, [handleWarningClose, warningMessages]);

  // Notify parent, if it cares, about the warning dialog, e.g. property editor should then not show its
  // validation tooltip (DT-035296).
  const warningIsShowing = !!warningMessages.length;
  const { onFileAreaWarningsShowing: onWarningsShowing } = props;
  useEffect(() => {
    onWarningsShowing?.(warningIsShowing);
  }, [onWarningsShowing, warningIsShowing]);

  let fileUploadAreaStyle: CSSProperties = {};

  //27 Pixel of extra padding hence control shows unnecessary scroll bar.

  const extraHeight = 27;

  if (props.setMinSize) {
    fileUploadAreaStyle.minHeight = props.requestedMinHeight ? props.requestedMinHeight - extraHeight : '';
  } else if (props.setMaxSize) {
    fileUploadAreaStyle.maxHeight = props.requestedMaxHeight ? props.requestedMaxHeight - extraHeight : '';
  }

  return (
    <div className={`dp-file-upload-area__root full-size ${props.containerCssClass || ''}`} style={fileUploadAreaStyle}>
      {uploadAreaView}
      {warningDialogView}
    </div>
  );
};
